import _ from 'lodash';
import type {
  EChartsOption,
  HeatmapSeriesOption,
  VisualMapComponentOption,
} from 'echarts';

import {
  FONT_SIZE,
  FormatMetric,
  toBaseOption,
  toTooltipHtml,
  toTooltipStyle,
} from './shared';

// How faint a drill-down leaves the rows (a Timeline's dates) it didn't
// pick
const DESELECTED_OPACITY = 0.25;
// Room the colour scale takes on the right
const SCALE_WIDTH = 64;
// The gap between a Timeline's cells
const TIMELINE_GAP = 2;

const toCategoryAxis = ({
  data,
  theme,
  isInverse,
}: {
  data: string[];
  theme: StudioCharts.Theme;
  isInverse: boolean;
}) => ({
  type: 'category' as const,
  data,
  inverse: isInverse,
  axisLine: { show: false },
  axisTick: { show: false },
  axisLabel: {
    color: theme.textPrimary,
    fontSize: FONT_SIZE,
    hideOverlap: true,
  },
  splitArea: { show: false },
});

// The colour scale beside a heatmap of two dimensions, from none up to the
// most
const toScale = ({
  model,
  theme,
  max,
  formatMetric,
}: {
  model: StudioCharts.HeatmapModel;
  theme: StudioCharts.Theme;
  max: number;
  formatMetric: FormatMetric;
}): VisualMapComponentOption => ({
  type: 'continuous',
  min: 0,
  max: max || 1,
  calculable: false,
  orient: 'vertical',
  right: 0,
  top: 'middle',
  itemHeight: 160,
  itemWidth: 12,
  // Its ends labelled: the most at the top, none at the bottom
  text: [
    formatMetric({ metricId: model.metricId, value: max }),
    formatMetric({ metricId: model.metricId, value: 0 }),
  ],
  inRange: { color: [model.minColor, model.maxColor] },
  textStyle: { color: theme.textMuted, fontSize: FONT_SIZE },
  formatter: (value) =>
    formatMetric({ metricId: model.metricId, value: value as number }),
});

// A Timeline's cells keep their own colours. ECharts draws no heatmap
// without a visualMap, so it has a hidden one that colours nothing.
const toCellColours = (
  model: StudioCharts.HeatmapModel,
): VisualMapComponentOption => {
  const values = model.cells.map((c) => c.value);
  return {
    type: 'continuous',
    show: false,
    min: Math.min(0, ...values),
    max: Math.max(1, ...values),
    inRange: { color: null },
    outOfRange: { color: null },
  } as VisualMapComponentOption;
};

// A heatmap: a cell per row (dimension A, down the side, first at the top)
// and column (dimension B, along the bottom), coloured from none up to the
// most on a scale beside it, as the Highcharts heatmap. A drill-down picks a
// row, fading the others (their cells keep the scale's colours). A Timeline
// has a row per metric and a column per date, its cells coloured by their
// row (see toTimelineHeatmapModel): no scale, as each row has its own, and
// a drill-down picks dates.
const toHeatmapOption = ({
  model,
  theme,
  formatMetric,
}: {
  model: StudioCharts.HeatmapModel;
  theme: StudioCharts.Theme;
  formatMetric: FormatMetric;
}): EChartsOption => {
  const { timeline } = model;
  const max = Math.max(0, ...model.cells.map((c) => c.value));
  // What a drill-down picks a cell by: its row, or a Timeline's date
  const toSelectionKey = (cell: { column: number; row: number }) =>
    timeline ? timeline.columns[cell.column]?.key : model.rows[cell.row];
  const series: HeatmapSeriesOption = {
    type: 'heatmap',
    id: model.seriesId,
    name: model.name,
    data: model.cells.map((cell) => {
      const key = toSelectionKey(cell);
      const isDeselected =
        !!model.selectedKeys &&
        key !== undefined &&
        !model.selectedKeys.includes(key);
      const itemStyle = {
        ...(cell.color ? { color: cell.color } : {}),
        ...(isDeselected ? { opacity: DESELECTED_OPACITY } : {}),
      };
      return {
        value: [cell.column, cell.row, cell.value],
        ...(_.isEmpty(itemStyle) ? {} : { itemStyle }),
      };
    }),
    itemStyle: {
      borderColor: theme.background,
      borderWidth: timeline ? TIMELINE_GAP : 1,
    },
    emphasis: { itemStyle: { borderColor: theme.textPrimary } },
  };
  // A Timeline's values are formatted by their row's metric
  const toMetricId = (row: number) =>
    timeline ? timeline.rows[row]?.metricId ?? model.metricId : model.metricId;
  return {
    ...toBaseOption(theme),
    grid: {
      left: 8,
      right: timeline ? 8 : SCALE_WIDTH,
      top: 12,
      bottom: 8,
      outerBoundsMode: 'same',
      outerBoundsContain: 'axisLabel',
    },
    xAxis: toCategoryAxis({ data: model.columns, theme, isInverse: false }),
    yAxis: toCategoryAxis({ data: model.rows, theme, isInverse: true }),
    visualMap: timeline
      ? toCellColours(model)
      : toScale({ model, theme, max, formatMetric }),
    tooltip: {
      ...toTooltipStyle(theme),
      trigger: 'item',
      formatter: (params) => {
        const item = Array.isArray(params) ? params[0] : params;
        const cell =
          item && typeof item.dataIndex === 'number'
            ? model.cells[item.dataIndex]
            : undefined;
        if (!cell) {
          return '';
        }
        return toTooltipHtml({
          title: model.rows[cell.row] ?? '',
          rows: [
            {
              name: timeline
                ? timeline.columns[cell.column]?.title ?? ''
                : model.columns[cell.column] ?? '',
              value: formatMetric({
                metricId: toMetricId(cell.row),
                value: cell.value,
              }),
            },
          ],
        });
      },
    },
    series: [series],
  };
};

export default toHeatmapOption;
